import { useCallback, useEffect, useMemo, useState } from 'react'
import SummaryDashboard from '../components/snoop/SummaryDashboard'
import {
  copyPgpAgendaHtml,
  downloadPgpAgendaCsv,
  fetchPgpAgenda,
  updatePubMappingLeadAr,
  updatePubMappingStatus,
} from '../api/pgpClient'
import {
  PGP_AGENDA_STATUS_SORT_RANK,
  PGP_LEAD_AR_OPTIONS,
  PGP_STATUS_OPTIONS,
  comparePgpAgendaStatus,
} from '../utils/snoop/statusOptions'
import { PLACEHOLDER_PGP_AGENDA } from '../utils/snoop/placeholderColumns'

const btnClass =
  'inline-flex shrink-0 items-center gap-2 rounded-lg border border-gray-300 bg-white px-3.5 py-2 text-sm font-medium text-gray-800 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60'

const btnActive =
  'inline-flex shrink-0 items-center gap-2 rounded-lg border border-violet-300 bg-violet-50 px-3.5 py-2 text-sm font-semibold text-violet-800 shadow-sm transition hover:bg-violet-100'

const PRESENT_MODE_KEY = 'pgp-agenda-present-mode'

const btnPrimaryFull =
  'inline-flex w-full items-center justify-center rounded-lg bg-gray-900 px-3.5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-60'
const btnSecondaryFull =
  'inline-flex w-full items-center justify-center rounded-lg border border-gray-300 bg-white px-3.5 py-2.5 text-sm font-medium text-gray-800 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60'

/** Copy-as-HTML (for email/docs) or download CSV. */
function PgpAgendaExportModal({ open, onClose }) {
  const [busy, setBusy] = useState(null)
  const [message, setMessage] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!open) return
    setBusy(null)
    setMessage(null)
    setError(null)
  }, [open])

  useEffect(() => {
    if (!open) return undefined
    function onKey(event) {
      if (event.key === 'Escape' && !busy) onClose?.()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose, busy])

  if (!open) return null

  async function run(kind, fn, done) {
    setBusy(kind)
    setError(null)
    setMessage(null)
    try {
      const result = await fn()
      setMessage(done(result))
    } catch (err) {
      setError(err?.message || String(err))
    } finally {
      setBusy(null)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pgp-agenda-export-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose?.()
      }}
    >
      <div className="w-full max-w-sm rounded-xl border border-gray-200 bg-white p-5 shadow-xl">
        <h2
          id="pgp-agenda-export-title"
          className="text-lg font-semibold tracking-tight text-gray-900"
        >
          Export Agenda
        </h2>
        <div className="mt-4 flex flex-col gap-2">
          <button
            type="button"
            className={btnPrimaryFull}
            disabled={Boolean(busy)}
            onClick={() =>
              run('html', () => copyPgpAgendaHtml({}), () => 'Copied HTML table to clipboard')
            }
            title="Formatted table with status sections — paste into email or a doc"
          >
            {busy === 'html' ? 'Copying…' : 'Copy as HTML'}
          </button>
          <button
            type="button"
            className={btnSecondaryFull}
            disabled={Boolean(busy)}
            onClick={() =>
              run('csv', () => downloadPgpAgendaCsv({}), (r) =>
                `Downloaded ${r?.filename || 'CSV file'}`,
              )
            }
          >
            {busy === 'csv' ? 'Downloading…' : 'Download CSV'}
          </button>
        </div>
        {message ? <p className="mt-3 text-sm text-green-700">{message}</p> : null}
        {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50"
            disabled={Boolean(busy)}
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

/** Status / Lead A&R edits need one of the two identities to address the row. */
function requireIdentity(mappingId, uid, label) {
  const hasId = mappingId != null && String(mappingId).trim() !== ''
  const hasUid = uid != null && String(uid).trim() !== ''
  if (!hasId && !hasUid) {
    throw new Error(`Missing Pub Mapping ID / Genius Artist ID for ${label}`)
  }
}

export default function PgpAgendaPage() {
  const [exportOpen, setExportOpen] = useState(false)
  const [presentMode, setPresentMode] = useState(() => {
    try {
      return window.localStorage.getItem(PRESENT_MODE_KEY) === '1'
    } catch {
      return false
    }
  })

  const fetchData = useCallback(() => fetchPgpAgenda(), [])

  useEffect(() => {
    try {
      window.localStorage.setItem(PRESENT_MODE_KEY, presentMode ? '1' : '0')
    } catch {
      /* storage disabled — toggle still works for this session */
    }
  }, [presentMode])

  useEffect(() => {
    // The export modal owns Escape while it is open.
    if (!presentMode || exportOpen) return undefined
    function onKey(event) {
      if (event.key === 'Escape') setPresentMode(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [presentMode, exportOpen])

  const editableStatus = useMemo(
    () => ({
      Status: {
        target: 'pub_mappings',
        uidColumn: 'Genius Artist ID',
        rowIdColumn: 'Pub Mapping ID',
        options: PGP_STATUS_OPTIONS,
        centerText: true,
        removeAfterSave: true,
        // Agenda only lists the four pipeline stages, so moving a row outside
        // them drops it from this view.
        shouldRemoveRow: (status) =>
          !(String(status ?? '').trim() in PGP_AGENDA_STATUS_SORT_RANK),
        timestampColumn: 'Updated At',
        onSave: async ({ status, row, mappingId, uid }) => {
          requireIdentity(mappingId, uid, 'status update')
          await updatePubMappingStatus({
            id: mappingId,
            geniusArtistId: uid,
            status,
            name: row?.Name,
            geniusUrl: row?.['Genius URL'],
          })
        },
      },
      'Lead A&R': {
        target: 'pub_mappings',
        uidColumn: 'Genius Artist ID',
        rowIdColumn: 'Pub Mapping ID',
        options: PGP_LEAD_AR_OPTIONS,
        centerText: true,
        allowBlank: true,
        timestampColumn: 'Updated At',
        onSave: async ({ status, mappingId, uid }) => {
          requireIdentity(mappingId, uid, 'Lead A&R update')
          await updatePubMappingLeadAr({
            id: mappingId,
            geniusArtistId: uid,
            leadAr: status,
          })
        },
      },
    }),
    [],
  )

  /**
   * Relative widths, rescaled to 100% across visible columns. Notes is the
   * column people actually read in the meeting, so it gets the most room; the
   * money columns are compact ($78.75k) and need little.
   */
  const columnWeights = useMemo(
    () => ({
      Name: 13,
      // Status + Lead are pill dropdowns: they need real width or the pill
      // runs into the next column.
      Status: 13,
      'Lead A&R': 11,
      Lawyer: 10,
      Calc: 7,
      'Committed $': 8,
      Uncommitted: 8,
      Option: 7,
      Notes: 23,
    }),
    [],
  )

  const defaultSort = useMemo(
    () => [
      { column: 'Status', direction: 'asc' },
      { column: 'Committed $', direction: 'desc' },
      { column: 'Calc', direction: 'desc' },
      { column: 'Lead A&R', direction: 'asc' },
    ],
    [],
  )

  const extraActions = (
    <>
    <button
      type="button"
      className={presentMode ? btnActive : btnClass}
      onClick={() => setPresentMode((on) => !on)}
      aria-pressed={presentMode}
      title={presentMode ? 'Exit present mode (Esc)' : 'Larger type for screen sharing'}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M4 5h16v10H4zM12 15v4m-4 0h8"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      {presentMode ? 'Exit present' : 'Present'}
    </button>
    <button
      type="button"
      className={btnClass}
      onClick={() => setExportOpen(true)}
      title="Copy as HTML for email/docs, or download CSV"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M12 3v12m0 0 4-4m-4 4-4-4M5 19h14"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      Export
    </button>
    </>
  )

  return (
    <>
      <SummaryDashboard
      title="Agenda"
      subtitle="Longform → Proposal → Priority → Watching"
      fetchData={fetchData}
      defaultSort={defaultSort}
      statusCompare={comparePgpAgendaStatus}
      searchPlaceholder="Search name or Genius artist id…"
      editableStatus={editableStatus}
      extraActions={extraActions}
      layout="mapping"
      presentMode={presentMode}
      columnWeights={columnWeights}
      wrapColumns={['Notes']}
      headerLabels={{ 'Lead A&R': 'Lead', Name: 'Artist' }}
      truncateColumns={['Name', 'Lawyer']}
      hiddenColumns={[
        'Pub Mapping ID',
        'Created At',
        'Genius URL',
        'Updated At',
        'Action',
        'Genius Artist ID',
      ]}
      placeholderColumns={PLACEHOLDER_PGP_AGENDA}
      tableKey="pgp-agenda"
      />
      <PgpAgendaExportModal open={exportOpen} onClose={() => setExportOpen(false)} />
    </>
  )
}
